
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Button } from './button';
import { DropdownSelect } from './dropdown-select';
import { useI18n } from '@/lib/i18n';

// UI_BUILD_SPEC §2.11. Range text on the left, size + stepper on the right, and
// a polite live region so a keyboard/screen-reader user learns the page changed
// without the focus jumping.

const PAGE_SIZES = ['25', '50', '100'].map((value) => ({ value, label: value }));

interface PaginationProps {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  className?: string;
}

export function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  className,
}: PaginationProps) {
  const { t } = useI18n();
  const totalPages = Math.max(Math.ceil(total / pageSize), 1);
  const first = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);

  return (
    <div className={cn('flex flex-wrap items-center justify-between gap-3', className)}>
      <p className="text-base text-ink-muted">
        {total === 0
          ? t('pagination.noResults')
          : t('pagination.showing', { first: first.toLocaleString(), last: last.toLocaleString(), total: total.toLocaleString() })}
      </p>

      <div className="flex items-center gap-2">
        {onPageSizeChange && (
          <DropdownSelect
            options={PAGE_SIZES}
            value={String(pageSize)}
            onChange={(value) => onPageSizeChange(Number(value))}
            triggerVariant="button"
            aria-label={t('pagination.resultsPerPage')}
            renderTrigger={(selected) => <span>{t('pagination.perPage', { count: selected?.label ?? pageSize })}</span>}
          />
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          aria-label={t('pagination.previousAria')}
        >
          <ChevronLeft aria-hidden="true" />
          {t('pagination.previous')}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          aria-label={t('pagination.nextAria')}
        >
          {t('pagination.next')}
          <ChevronRight aria-hidden="true" />
        </Button>
      </div>

      <span aria-live="polite" className="sr-only">
        {t('pagination.pageOf', { page, total: totalPages })}
      </span>
    </div>
  );
}
